import { gsap } from 'gsap';
import type { ThreeStage } from '../../../three/ThreeStage';
import { BaseScene } from '../../../core/BaseScene';
import { words, revealWords, settleWords } from '../../../components/text';
import { productReveal, playProductReveal, settleProductReveal } from '../../../components/reveal';

/** The focused roles named in the original showcase content (MODERTA-ORCHESTRATE.md §9). */
const ROLES = ['Research', 'Requirements', 'Design audit', 'Test design', 'Engineering quality', 'Security review'];
/** Where each draft card settles on the 1600×900 artboard, with a slight hand-placed tilt. */
const SPOTS: ReadonlyArray<[x: number, y: number, r: number]> = [
  [170, 190, -2.5], [520, 120, 1.5], [880, 180, -1], [1230, 130, 2],
  [330, 470, 1.8], [1060, 480, -1.6],
];
const AI = ['Reason', 'Suggest', 'Review', 'Explain'];
const AUTOMATION = ['Template', 'Create', 'Connect', 'Validate', 'Move'];

/**
 * Research Space follows its own guideline on the stage: flat #E0E0E0 canvas, white cards, no
 * gradient, no shadow, a serif for titles, mono for identifiers, status colour only as dots.
 *
 * 0 Draft cards from six focused roles — Many focused tools. Many drafts.
 * 1 Every card waits for review — Which version is true?
 * 2 The lockup: Moderta AI — Research Space. AI proposes — people approve.
 * 3 AI for judgment, automation for the repeatable — The right tool for each step.
 */
export class ResearchSpaceScene extends BaseScene {
  public readonly stepCount = 4;

  protected template(): string {
    const drafts = ROLES.map((role, i) => {
      const [x, y, r] = SPOTS[i];
      return `<article class="rs-card rs-draft" data-index="${i}" style="left:${x}px;top:${y}px;--tilt:${r}deg">
        <span class="rs-mono">D-${String(i + 11).padStart(2, '0')} · Draft</span>
        <h3 class="rs-serif">Draft from ${role.toLowerCase()}</h3>
        <i class="rs-bar"></i><i class="rs-bar rs-bar--short"></i>
        <span class="rs-badge rs-badge--review"><i></i>Needs review</span>
      </article>`;
    }).join('');

    const column = (title: string, eyebrow: string, items: string[], kind: 'ai' | 'auto'): string => `
      <section class="rs-card rs-column rs-column--${kind}">
        <span class="rs-eyebrow">${eyebrow}</span>
        <h3 class="rs-serif">${title}</h3>
        <ol>${items.map((item, i) => `<li><span class="rs-mono">${String(i + 1).padStart(2, '0')}</span>${item}</li>`).join('')}</ol>
      </section>`;

    return `
      <div class="scene-layout rs-scene rs-intro">
        <div class="artboard">
          <div class="rs-drafts" aria-hidden="true">${drafts}</div>
          <div class="rs-columns" data-from="3">
            ${column('Judgment', 'AI', AI, 'ai')}
            <div class="rs-columns__divider" aria-hidden="true"><span class="rs-mono">The right tool</span></div>
            ${column('The repeatable', 'Automation', AUTOMATION, 'auto')}
          </div>
          <div class="rs-statements">
            <h2 class="rs-statement" data-from="0" data-until="0">${words('Many focused tools. Many drafts.')}</h2>
            <h2 class="rs-statement" data-from="1" data-until="1">${words('Which version is true?')}</h2>
            <h2 class="rs-statement" data-from="3">${words('The right tool for each step.')}</h2>
          </div>
          <p class="rs-caption">Conceptual visualisation in the Research Space design language. Not a product screenshot.</p>
        </div>
        ${productReveal('research-space', 2, '', 2)}
      </div>`;
  }

  protected onEnter(immediate: boolean): void {
    if (immediate || this.step !== 0) return;
    const tl = this.timeline();
    tl.fromTo(this.$$('.rs-draft'), { opacity: 0, y: 26, rotate: 0 }, { opacity: 1, y: 0, rotate: (i, el: HTMLElement) => el.style.getPropertyValue('--tilt'), duration: 0.9, stagger: 0.11, ease: 'expo.out' }, 0.35);
    tl.add(revealWords(this.$('.rs-statement')), 1.1);
  }

  protected onStep(step: number, stage: ThreeStage, immediate: boolean, previousStep: number): void {
    stage.setAccent(this.meta.accent);
    stage.setMode(this.meta.id, step, immediate);
    const forward = !immediate && step > previousStep;
    const d = immediate ? 0 : 1;
    const tl = this.timeline();

    const statements = this.$$('.rs-statement');
    statements.forEach((s) => settleWords(s));
    if (forward && step === 1) tl.add(revealWords(statements[1]), 0.3);
    if (forward && step === 3) tl.add(revealWords(statements[2]), 0.9);

    // Drafts: present and waiting (0–1), cleared for the lockup (2+).
    const drafts = this.$$('.rs-draft');
    if (step <= 1) {
      gsap.to(drafts, { opacity: step === 1 ? 0.62 : 1, x: 0, y: 0, scale: 1, rotate: (i, el: HTMLElement) => el.style.getPropertyValue('--tilt'), duration: d * 0.6, overwrite: 'auto' });
      if (forward && step === 1) {
        tl.fromTo(this.$$('.rs-badge--review i'), { scale: 1 }, { scale: 1.9, duration: 0.28, yoyo: true, repeat: 1, stagger: 0.06, ease: 'sine.inOut' }, 0.1);
      }
    } else if (forward && step === 2) {
      // The drafts gather toward the centre and step aside for the lockup.
      tl.to(drafts, { x: (i, el: HTMLElement) => 800 - (el.offsetLeft + 150), y: (i, el: HTMLElement) => 450 - (el.offsetTop + 80), scale: 0.6, rotate: 0, opacity: 0, duration: 0.9, stagger: 0.04, ease: 'power3.in' }, 0);
    } else {
      gsap.set(drafts, { opacity: 0 });
    }

    if (forward && step === 2) playProductReveal(this.root, tl, 0.85);
    else settleProductReveal(this.root);

    const columns = this.$$('.rs-column');
    const rows = this.$$('.rs-column li');
    if (forward && step === 3) {
      tl.fromTo(columns, { opacity: 0, y: 18 }, { opacity: 1, y: 0, duration: 0.7, stagger: 0.18, ease: 'expo.out' }, 0.1);
      tl.fromTo(rows, { opacity: 0, x: -8 }, { opacity: 1, x: 0, duration: 0.45, stagger: 0.05, ease: 'power2.out' }, 0.45);
      tl.fromTo(this.$('.rs-columns__divider'), { scaleY: 0 }, { scaleY: 1, duration: 0.8, ease: 'expo.inOut' }, 0.3);
    } else {
      gsap.set(columns, { opacity: 1, y: 0 });
      gsap.set(rows, { opacity: 1, x: 0 });
      gsap.set(this.$('.rs-columns__divider'), { scaleY: 1 });
    }
  }
}
